import { destroy2, get2, post2, put2 } from "@/shared/axios";
import {
  errorToast,
  getValueByAll,
  getValueByAllInArray,
} from "@/shared/utils/helpers";
import { ReceiptsDataResponse } from "@/shared/utils/interfaces";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useBranchId from "./useBranchId";

const useReceiptsHook = () => {
  const { t } = useTranslation();
  const branch = useBranchId();

  const query = useQuery<ReceiptsDataResponse[]>({
    queryKey: ["receiptSettings", branch],
    queryFn: () => get2(`/internal/secure/pos/v1/receiptSettings/${branch}`),
    select: (data: any) => {
      const defaultList =
        data.default?.map((item) =>
          Object.keys(item).reduce((acc, cur) => {
            acc[cur] =
              cur === "sources" || cur === "information_positions"
                ? getValueByAllInArray(item[cur])
                : getValueByAll(item[cur]);
            acc["type"] = "default";
            return acc;
          }, {})
        ) || [];

      const customList =
        data.custom?.map((item) =>
          Object.keys(item).reduce((acc, cur) => {
            acc[cur] =
              cur === "sources" ||
              cur === "information_positions" ||
              cur === "not_apply_on_item_ids" ||
              cur === "apply_on_item_ids" ||
              cur === "targets" ||
              cur === "category_display_order"
                ? getValueByAllInArray(item[cur])
                : getValueByAll(item[cur]);
            acc["type"] = "custom";
            return acc;
          }, {})
        ) || [];

      return [...defaultList, ...customList] as ReceiptsDataResponse[];
    },
  });

  const updateReceipt = useMutation<unknown, unknown, any>({
    mutationKey: ["update-receipt", branch],
    mutationFn: ({ item_id, ...data }) =>
      put2(`internal/secure/pos/v1/receiptSettings/${branch}/${item_id}`, data),
    onSuccess: () => {
      toast.success(t("success.update"));
    },
    onError: async () => {
      errorToast();
    },
  });

  const createReceipt = useMutation<unknown, unknown, any>({
    mutationKey: ["create-receipt", branch],
    mutationFn: ({ item_id, ...data }) =>
      post2(`internal/secure/pos/v1/receiptSettings/${branch}`, data),
    onSuccess: () => {
      toast.success(t("success.create"));
    },
    onError: async () => {
      errorToast();
    },
  });

  const removeReceipt = useMutation<unknown, unknown, any>({
    mutationKey: ["remove-receipt", branch],
    mutationFn: (item_id) =>
      destroy2(`internal/secure/pos/v1/receiptSettings/${branch}/${item_id}`),
    onSuccess: () => {
      toast.success(t("success.delete"));
    },
    onError: async (error: AxiosError) => {
      if (error.response.status === 406) {
        errorToast(
          "Boninstelling wordt gebruikt in printerinstellingen. Als u deze instelling wilt verwijderen, verwijdert u het gebruik op de pagina met printerinstellingen!"
        );
      } else {
        errorToast();
      }
    },
  });

  return { query, updateReceipt, createReceipt, removeReceipt };
};

export default useReceiptsHook;
